// ─────────────────────────────────────────────────────────────────────────────
// PageHero.jsx — MIAVIT style v2
// Green section hero used at top of every inner page
// Amber top accent line, breadcrumbs, title
//
// Usage:
//   <PageHero
//     title="About Us"
//     breadcrumbs={[
//       { label: 'Home', to: '/' },
//       { label: 'About Us' },            ← last item = no link = amber color
//     ]}
//   />
//
//   <PageHero
//     title="Products"
//     subtitle="Browse our full range of solutions"
//     breadcrumbs={[
//       { label: 'Home', to: '/' },
//       { label: 'Products', to: '/products' },
//       { label: 'Feed Additives' },
//     ]}
//   />
// ─────────────────────────────────────────────────────────────────────────────

import { Link } from 'react-router-dom'

// Breadcrumb chevron
const Chevron = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
    style={{ opacity: 0.4, flexShrink: 0 }}>
    <path d="M4.5 2.5L7.5 6l-3 3.5"
      stroke="white" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
)

export default function PageHero({
  title,
  subtitle,
  breadcrumbs = [],   // [{ label: 'Home', to: '/' }, { label: 'About Us' }]
  minHeight   = '120px',
  style: extraStyle = {},
}) {
  return (
    <section style={{
      background: '#007935',
      padding:    'clamp(28px, 4vw, 44px) 0 clamp(24px, 3.5vw, 40px)',
      position:   'relative',
      overflow:   'hidden',
      ...extraStyle,
    }}>
      {/* Amber top accent line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: '3px', background: '#ed9716',
      }} />

      <div style={{
        maxWidth: 'var(--container-max)', margin: '0 auto',
        padding:  '0 var(--container-padding)',
        minHeight,
        display:  'flex', flexDirection: 'column', justifyContent: 'center',
      }}>

        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <nav
            aria-label="Breadcrumb"
            style={{
              display:     'flex', alignItems: 'center',
              gap:         '6px', flexWrap: 'wrap',
              marginBottom: '14px',
            }}
          >
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1
              return (
                <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {crumb.to && !isLast ? (
                    <Link
                      to={crumb.to}
                      style={{
                        fontFamily:     "'DM Sans', sans-serif",
                        fontSize:       '12px', fontWeight: 500,
                        color:          'rgba(255,255,255,0.65)',
                        textDecoration: 'none',
                        transition:     'color 0.2s ease',
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize:   '12px', fontWeight: isLast ? 600 : 500,
                      color:      isLast ? '#ed9716' : 'rgba(255,255,255,0.65)',
                    }}>
                      {crumb.label}
                    </span>
                  )}
                  {!isLast && <Chevron />}
                </span>
              )
            })}
          </nav>
        )}

        {/* Title */}
        {title && (
          <h1 style={{
            fontFamily:    "'DM Sans', sans-serif",
            fontSize:      'clamp(1.5rem, 3vw, 2.2rem)',
            fontWeight:    400,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color:         '#ffffff',
            margin:        subtitle ? '0 0 10px' : '0',
            lineHeight:    1.2,
          }}>
            {title}
          </h1>
        )}

        {/* Optional subtitle */}
        {subtitle && (
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize:   'clamp(13.5px, 1.3vw, 15px)',
            fontWeight: 400,
            color:      'rgba(255,255,255,0.80)',
            lineHeight: 1.65, margin: 0,
            maxWidth:   '800px',
          }}>
            {subtitle}
          </p>
        )}

      </div>
    </section>
  )
}